import React, { useState, useEffect } from "react";
import GuideSection from "../components/guide/GuideSection";
import { documentsGuide } from "../components/guide/guideContent";
import { useDropzone } from "react-dropzone";
import { Alert, Button, ListGroup } from "react-bootstrap";
import { FaRegTimesCircle } from "react-icons/fa";
import { MdOutlineUploadFile } from "react-icons/md";
import { FiFile } from "react-icons/fi";
import { LiaFilePdf } from "react-icons/lia";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import { Link, useNavigate } from "react-router-dom";
import { addBusinessCreationRequestDocuments } from "../redux/actions/DocumentActions";
import Loader from "../components/loader/Loader";
import "./styles/flow-form.css";

// Mapping des documents
const documentTypes = {
  mgId: "La photocopie de votre pièce d’identité (Passeport ou carte de séjour pour les étrangers)",
  location:
    "Le plan de localisation de l'entreprise, avec le numéro de téléphone et la signature directement inscrits sur le plan.",
  mgCrimRecord: "L'Extrait du casier judiciaire du ou des gérant",
  mgWedCert: "Acte de mariage",
  shdIds:
    "Les photocopies des pièces d’identités des associés (Passeport ou carte de séjour pour les étrangers ou acte de naissance pour les associés mineurs)",
  verbalReport:
    "Liste conforme des associés ainsi que la répartition des parts de chacun.",
  assocStatutes: "Les statuts de l'association, datés et signés.",
  assocRules: "Le règlement intérieur de l'association.",
  assocAssemblyReport: "Le procès-verbal de l'assemblée générale constitutive.",
};

// Documents requis selon le type d'entreprise
const documentsByBusinessType = {
  ETS: ["mgId", "location"],
  SARL: ["mgId", "shdIds", "verbalReport", "location"],
  SARLU: ["mgId", "location"],
  SA: ["mgId", "shdIds", "verbalReport", "location"],
  SAS: ["mgId", "shdIds", "verbalReport", "location"],
  SASU: ["mgId", "location"],
  SNC: ["mgId", "shdIds", "verbalReport", "location"],
  SCOOPS: ["mgId", "shdIds", "verbalReport", "location"],
  ASSOCIATION: [
    "mgId",
    "assocStatutes",
    "assocRules",
    "assocAssemblyReport",
    "location",
  ],
};

function Documents() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.authUser);
  const { businessCreationRequest } = useSelector((state) => state.request);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [currentDocument, setCurrentDocument] = useState("");
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [animate, setAnimate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (cart.businessType && documentsByBusinessType[cart.businessType]) {
      let docs = [...documentsByBusinessType[cart.businessType]];

      if (cart.maritalStatus === "single") {
        docs = docs.filter((doc) => doc !== "mgWedCert");
      }

      setRequiredDocuments(docs);
      setUploadedFiles([]);
      setCurrentDocument(docs[0] || "");
    }
  }, [cart.businessType, cart.maritalStatus]);

  const handleButtonClick = async () => {
    setSubmitting(true);

    try {
      const formData = await new Promise((resolve, reject) => {
        try {
          const formData = new FormData();
          uploadedFiles.forEach((file) => {
            const key = file.documentType;

            if (key && file.file) {
              formData.append(key, file.file);
            }
          });
          resolve(formData);
        } catch (error) {
          reject(error);
        }
      });

      await dispatch(
        addBusinessCreationRequestDocuments(
          user.token,
          businessCreationRequest.reference,
          formData,
          handleCallback,
        ),
      );
    } catch (error) {
      console.error("Erreur lors de la création de FormData :", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      navigate("/company-seat");
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  const onDrop = (acceptedFiles) => {
    if (!currentDocument) return;

    const newFile = acceptedFiles[0];

    const content = {
      file: newFile,
      name: newFile.name,
      id: Math.random().toString(36).substring(2, 9),
      documentType: currentDocument,
      uri: newFile.path || newFile.uri,
      type: newFile.type,
    };

    setUploadedFiles((prevFiles) => [...prevFiles, content]);

    Swal.fire({
      icon: "success",
      title: "Succès",
      text: `${documentTypes[currentDocument]} ajouté.`,
      toast: true,
      position: "top-right",
      showConfirmButton: false,
      timer: 2000,
    });

    const remainingDocs = requiredDocuments.filter(
      (doc) => !uploadedFiles.some((file) => file.documentType === doc),
    );

    if (remainingDocs.length > 1) {
      setCurrentDocument(remainingDocs[1]);
      setAnimate(true);
      setTimeout(() => setAnimate(false), 1000);
    } else {
      setCurrentDocument("");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
  });

  const removeFile = (id, documentType) => {
    setUploadedFiles(uploadedFiles.filter((file) => file.id !== id));
    setCurrentDocument(documentType);
  };

  const isAllDocumentsUploaded =
    uploadedFiles.length === requiredDocuments.length &&
    requiredDocuments.every((doc) =>
      uploadedFiles.some((file) => file.documentType === doc),
    );

  return (
    <div style={{ backgroundColor: "#fff", paddingBottom: "40px" }}>
      <Header />

      <ProgressBar
        steps={[
          "Propriétaire",
          "Entreprise",
          "Services",
          "Paiement",
          "Finalisation",
        ]}
        currentStep="Entreprise"
      />
      <Loader isLoading={submitting} />
      <div
        className="container mt-3 px-3 text-center"
        style={{ maxWidth: "700px" }}
      >
        <h4 className="text-center mt-4">
          Ajoutez les <span style={{ color: "#FF5500" }}>documents</span>{" "}
          nécessaires pour la création de votre {cart?.businessType}
        </h4>
        <p className="text-muted">
          Vous n’êtes pas en possession de tous vos documents ? Cliquez ici pour
          les fournir plus tard à nos opérateurs via WhatsApp :{" "}
          <Link to={"/company-seat"}>Fournir plus tard via WhatsApp </Link>
        </p>
      </div>

      <div className="container mt-4 text-center" style={{ maxWidth: "620px" }}>
        <div
          {...getRootProps()}
          className={`doc-dropzone ${isDragActive ? "is-active" : ""}`}
        >
          <input {...getInputProps()} />
          <MdOutlineUploadFile size={40} color="gray" />
          <p style={{ fontSize: "16px", color: "#333" }}>
            {isDragActive ? (
              "Déposez votre fichier ici..."
            ) : (
              <>
                Glissez-déposez ou cliquez ici pour ajouter le fichier contenant
                :
                <span
                  style={{
                    color: "#FF5500",
                    fontWeight: "500",
                    transition: "background-color 0.5s ease",
                    backgroundColor: animate ? "#FFF3CD" : "transparent",
                    padding: "5px",
                    borderRadius: "5px",
                  }}
                >
                  {documentTypes[currentDocument] || (
                    <span
                      style={{
                        color: "green",
                        fontWeight: "500",
                        transition: "background-color 0.5s ease",
                        backgroundColor: animate ? "#D4EDDA" : "transparent",
                        padding: "5px",
                        borderRadius: "5px",
                      }}
                    >
                      {isAllDocumentsUploaded
                        ? 'Tous les documents sont ajoutés ! Appuyez sur le "bouton" Continuer ci-dessous pour poursuivre.'
                        : ""}
                    </span>
                  )}
                </span>
              </>
            )}
          </p>

          <p style={{ color: "#777", fontSize: "14px" }}>
            Formats acceptés : <LiaFilePdf size={20} /> PDF uniquement
          </p>
        </div>

        {/* Liste des fichiers téléchargés */}
        {uploadedFiles.length > 0 ? (
          <div className="mt-4">
            <h6>Fichiers ajoutés :</h6>
            <ListGroup>
              {uploadedFiles.map((file) => (
                <ListGroup.Item
                  key={file.id}
                  className="d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex align-items-center">
                    <FiFile style={{ marginRight: "5px" }} size={20} />
                    {file.name}
                  </div>
                  <FaRegTimesCircle
                    onClick={() => removeFile(file.id, file.documentType)}
                    style={{
                      cursor: "pointer",
                      fontSize: "20px",
                      color: "red",
                    }}
                  />
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        ) : (
          <Alert className="mt-4" variant="info">
            Aucun fichier ajouté pour le moment.
          </Alert>
        )}

        {/* Bouton pour continuer */}
        <div className="d-flex justify-content-center mt-4">
          <Button
            disabled={!isAllDocumentsUploaded}
            className="flow-submit"
            onClick={handleButtonClick}
          >
            Continuer
          </Button>

          <div className="container mt-3 px-3 text-center">
            <Link to={"/company-seat"}>Fournir plus tard via WhatsApp </Link>
          </div>
        </div>
      </div>

      <GuideSection {...documentsGuide} />
    </div>
  );
}

export default Documents;
